import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { PushService } from '../../services/push.service';
import { SupabaseService } from '../../services/supabase.service';

const DISMISS_KEY = 'actively_push_prompt_dismissed_at';
const DISMISS_DAYS = 7;

/**
 * Admin nudge: install the app / enable push notifications.
 *
 * Visit requests and (soon) paid orders die when admins answer slowly — push
 * is the channel that reaches them off-app. Shown ONLY to club admins while
 * push isn't enabled:
 *  - Android/desktop with a captured beforeinstallprompt → "Install" CTA that
 *    triggers the native prompt, then flows into enabling notifications.
 *  - iOS Safari (web push requires the installed PWA) → Add to Home Screen
 *    instructions.
 *  - Otherwise → a direct "Enable notifications" CTA.
 * Dismissal snoozes it for a week; it disappears for good once enabled.
 */
@Component({
  selector: 'app-push-prompt',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    @if (visible) {
      <div class="pp" role="region" [attr.aria-label]="'pushPrompt.title' | translate">
        <span class="pp__ico" aria-hidden="true"><i class="pi pi-bell"></i></span>
        <div class="pp__main">
          <p class="pp__title">{{ 'pushPrompt.title' | translate }}</p>
          @if (mode === 'ios-install') {
            <p class="pp__body">{{ 'pushPrompt.iosBody' | translate }}</p>
            <p class="pp__steps">{{ 'pushPrompt.iosSteps' | translate }}</p>
          } @else {
            <p class="pp__body">{{ 'pushPrompt.body' | translate }}</p>
            <div class="pp__actions">
              @if (mode === 'install') {
                <button type="button" class="ap-btn ap-btn--filled ap-btn--sm" (click)="install()">
                  {{ 'pushPrompt.installCta' | translate }}
                </button>
              }
              @if (mode === 'enable') {
                <button type="button" class="ap-btn ap-btn--filled ap-btn--sm" [disabled]="busy" (click)="enable()">
                  {{ (busy ? 'pushPrompt.enabling' : 'pushPrompt.enableCta') | translate }}
                </button>
              }
              @if (failed) {
                <span class="pp__err" role="alert">{{ 'pushPrompt.failed' | translate }}</span>
              }
            </div>
          }
        </div>
        <button
          type="button"
          class="pp__close ap-focus"
          [attr.aria-label]="'pushPrompt.dismiss' | translate"
          (click)="dismiss()"
        >
          ×
        </button>
      </div>
    }
  `,
  styles: [
    `
      .pp {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        margin: 0 auto 18px;
        max-width: var(--ap-maxw, 1100px);
        padding: 14px 16px;
        border: 1px solid var(--ap-hairline, #e5e5e5);
        border-radius: 14px;
        background: var(--ap-green-tint, #e8f5ec);
      }
      .pp__ico {
        width: 34px;
        height: 34px;
        flex-shrink: 0;
        display: grid;
        place-items: center;
        border-radius: 10px;
        background: #fff;
        color: var(--ap-green, #15803d);
      }
      .pp__main {
        min-width: 0;
      }
      .pp__title {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        color: var(--ap-ink, #1d1d1f);
      }
      .pp__body {
        margin: 2px 0 0;
        font-size: 13px;
        color: var(--ap-ink-2, #424245);
      }
      .pp__steps {
        margin: 6px 0 0;
        font-size: 13px;
        font-weight: 500;
        color: var(--ap-ink, #1d1d1f);
      }
      .pp__actions {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-top: 10px;
      }
      .pp__err {
        font-size: 12px;
        color: #b42318;
      }
      .pp__close {
        margin-left: auto;
        flex-shrink: 0;
        border: 0;
        background: none;
        color: var(--ap-ink-3, #86868b);
        font-size: 18px;
        line-height: 1;
        cursor: pointer;
        padding: 2px 6px;
      }
    `,
  ],
})
export class PushPromptComponent implements OnInit {
  private readonly push = inject(PushService);
  private readonly supabase = inject(SupabaseService);

  visible = false;
  mode: 'enable' | 'install' | 'ios-install' = 'enable';
  busy = false;
  failed = false;

  private isAdmin = false;
  private deferredInstall: any = null;

  @HostListener('window:beforeinstallprompt', ['$event'])
  onBeforeInstall(e: Event): void {
    e.preventDefault();
    this.deferredInstall = e;
    this.evaluate();
  }

  @HostListener('window:appinstalled')
  onInstalled(): void {
    this.deferredInstall = null;
    this.push.refresh();
    this.evaluate();
  }

  async ngOnInit(): Promise<void> {
    try {
      const user = await this.supabase.getCurrentUser();
      if (!user) return;
      const clubs = await this.supabase.getUserClubs(user.id);
      this.isAdmin = (clubs ?? []).some(
        (c) => c.role === 'admin' || c.role === 'super_admin'
      );
      this.evaluate();
    } catch {
      // stays hidden
    }
  }

  private evaluate(): void {
    const state = this.push.state();
    const snoozedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const snoozed =
      snoozedAt > 0 && Date.now() - snoozedAt < DISMISS_DAYS * 86400000;
    if (!this.isAdmin || snoozed || state === 'enabled' || state === 'denied' || state === 'unsupported') {
      this.visible = false;
      return;
    }
    if (state === 'needs-install') {
      this.mode = 'ios-install';
    } else if (this.deferredInstall && !this.push.isStandalone) {
      this.mode = 'install';
    } else {
      this.mode = 'enable';
    }
    this.visible = true;
  }

  async install(): Promise<void> {
    const prompt = this.deferredInstall;
    if (!prompt) return;
    this.deferredInstall = null;
    try {
      await prompt.prompt();
      await prompt.userChoice;
    } finally {
      this.evaluate();
    }
  }

  async enable(): Promise<void> {
    this.busy = true;
    this.failed = false;
    const result = await this.push.enable();
    this.busy = false;
    this.failed = result === 'error';
    this.evaluate();
    if (result === 'error') this.visible = true;
  }

  dismiss(): void {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    this.visible = false;
  }
}
